import { useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// Types — mirror the Rust ProvenanceRecord / enums
// ---------------------------------------------------------------------------

type ProvenanceAction =
  | "ChatCompletion"
  | "CanvasGeneration"
  | "CodeReview"
  | "RecipeExecution"
  | "ToolCall";

type ApprovalStatus =
  | "AutoApproved"
  | "PendingReview"
  | "HumanApproved"
  | "HumanRejected";

interface ProvenanceRecord {
  id: string;
  timestamp: string;
  action: ProvenanceAction;
  provider: string;
  model: string;
  prompt_hash: string;
  input_tokens: number;
  output_tokens: number;
  duration_ms: number;
  approval: ApprovalStatus;
  metadata: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ALL_ACTIONS: ProvenanceAction[] = [
  "ChatCompletion",
  "CanvasGeneration",
  "CodeReview",
  "RecipeExecution",
  "ToolCall",
];

const ALL_APPROVALS: ApprovalStatus[] = [
  "AutoApproved",
  "PendingReview",
  "HumanApproved",
  "HumanRejected",
];

const ACTION_LABELS: Record<ProvenanceAction, string> = {
  ChatCompletion: "Chat",
  CanvasGeneration: "Canvas",
  CodeReview: "Review",
  RecipeExecution: "Recipe",
  ToolCall: "Tool",
};

const ACTION_ACCENTS: Record<ProvenanceAction, string> = {
  ChatCompletion: "mint",
  CanvasGeneration: "violet",
  CodeReview: "cyan",
  RecipeExecution: "amber",
  ToolCall: "slate",
};

const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  AutoApproved: "Auto",
  PendingReview: "Pending",
  HumanApproved: "Approved",
  HumanRejected: "Rejected",
};

const APPROVAL_TONES: Record<ApprovalStatus, string> = {
  AutoApproved: "good",
  PendingReview: "warn",
  HumanApproved: "good",
  HumanRejected: "bad",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function generateAuditMarkdown(records: ProvenanceRecord[]): string {
  const lines: string[] = [];
  lines.push("# Provenance Audit Report");
  lines.push("");
  lines.push(`Generated: ${new Date().toISOString()}`);
  lines.push(`Total records: ${records.length}`);
  lines.push("");
  lines.push("## Summary by Action Type");
  lines.push("");
  lines.push("| Action | Count | Input Tokens | Output Tokens |");
  lines.push("|--------|------:|-------------:|--------------:|");
  for (const action of ALL_ACTIONS) {
    const matching = records.filter((r) => r.action === action);
    if (!matching.length) continue;
    const inp = matching.reduce((s, r) => s + r.input_tokens, 0);
    const out = matching.reduce((s, r) => s + r.output_tokens, 0);
    lines.push(`| ${ACTION_LABELS[action]} | ${matching.length} | ${inp} | ${out} |`);
  }
  lines.push("");
  lines.push("## Approval Breakdown");
  lines.push("");
  lines.push("| Status | Count |");
  lines.push("|--------|------:|");
  for (const status of ALL_APPROVALS) {
    const count = records.filter((r) => r.approval === status).length;
    if (!count) continue;
    lines.push(`| ${APPROVAL_LABELS[status]} | ${count} |`);
  }
  lines.push("");
  lines.push("## Timeline");
  lines.push("");
  lines.push("| # | Timestamp | Action | Provider | Model | Tokens | Approval |");
  lines.push("|---|-----------|--------|----------|-------|-------:|---------:|");
  records.forEach((r, i) => {
    lines.push(
      `| ${i + 1} | ${r.timestamp} | ${ACTION_LABELS[r.action]} | ${r.provider} | ${r.model} | ${r.input_tokens}/${r.output_tokens} | ${APPROVAL_LABELS[r.approval]} |`,
    );
  });
  return lines.join("\n");
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Demo data — shown until real IPC data arrives
// ---------------------------------------------------------------------------

const DEMO_RECORDS: ProvenanceRecord[] = [
  {
    id: "1",
    timestamp: "2026-03-31T14:22:08Z",
    action: "ChatCompletion",
    provider: "anthropic",
    model: "claude-opus-4",
    prompt_hash: "a1b2c3d4e5f6",
    input_tokens: 2048,
    output_tokens: 1536,
    duration_ms: 3200,
    approval: "AutoApproved",
    metadata: {},
  },
  {
    id: "2",
    timestamp: "2026-03-31T14:23:11Z",
    action: "CanvasGeneration",
    provider: "anthropic",
    model: "claude-opus-4",
    prompt_hash: "f6e5d4c3b2a1",
    input_tokens: 512,
    output_tokens: 2048,
    duration_ms: 4100,
    approval: "HumanApproved",
    metadata: {},
  },
  {
    id: "3",
    timestamp: "2026-03-31T14:25:44Z",
    action: "CodeReview",
    provider: "openai",
    model: "o3",
    prompt_hash: "deadbeef0001",
    input_tokens: 4096,
    output_tokens: 768,
    duration_ms: 2800,
    approval: "PendingReview",
    metadata: {},
  },
  {
    id: "4",
    timestamp: "2026-03-31T14:27:02Z",
    action: "ToolCall",
    provider: "anthropic",
    model: "claude-sonnet-4",
    prompt_hash: "cafe0123abcd",
    input_tokens: 128,
    output_tokens: 64,
    duration_ms: 410,
    approval: "AutoApproved",
    metadata: {},
  },
  {
    id: "5",
    timestamp: "2026-03-31T14:28:59Z",
    action: "RecipeExecution",
    provider: "ollama",
    model: "llama-3.3-70b",
    prompt_hash: "bead4567dcba",
    input_tokens: 1024,
    output_tokens: 3072,
    duration_ms: 8900,
    approval: "HumanRejected",
    metadata: {},
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="provenance__stat">
      <span className="provenance__stat-value">{value}</span>
      <span className="provenance__stat-label">{label}</span>
    </div>
  );
}

function ActionBadge({ action }: { action: ProvenanceAction }) {
  return (
    <span className={`provenance__badge provenance__badge--${ACTION_ACCENTS[action]}`}>
      {ACTION_LABELS[action]}
    </span>
  );
}

function ApprovalBadge({ status }: { status: ApprovalStatus }) {
  return (
    <span className={`provenance__approval provenance__approval--${APPROVAL_TONES[status]}`}>
      {APPROVAL_LABELS[status]}
    </span>
  );
}

function TimelineEntry({ record }: { record: ProvenanceRecord }) {
  return (
    <div className="provenance__entry">
      <div className="provenance__entry-time">{formatTimestamp(record.timestamp)}</div>
      <ActionBadge action={record.action} />
      <div className="provenance__entry-provider">{record.provider}</div>
      <div className="provenance__entry-model">{record.model}</div>
      <div className="provenance__entry-tokens">
        <span className="provenance__tok-in">{formatNumber(record.input_tokens)}</span>
        <span className="provenance__tok-sep">/</span>
        <span className="provenance__tok-out">{formatNumber(record.output_tokens)}</span>
      </div>
      <div className="provenance__entry-duration">{record.duration_ms}ms</div>
      <ApprovalBadge status={record.approval} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main workbench
// ---------------------------------------------------------------------------

export function ProvenanceWorkbench({ payload }: { payload?: unknown }) {
  const records: ProvenanceRecord[] =
    Array.isArray(payload) ? (payload as ProvenanceRecord[]) : DEMO_RECORDS;

  const [filterAction, setFilterAction] = useState<ProvenanceAction | "all">("all");
  const [filterApproval, setFilterApproval] = useState<ApprovalStatus | "all">("all");
  const [filterProvider, setFilterProvider] = useState<string>("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const providers = useMemo(
    () => Array.from(new Set(records.map((r) => r.provider))).sort(),
    [records],
  );

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (filterAction !== "all" && r.action !== filterAction) return false;
      if (filterApproval !== "all" && r.approval !== filterApproval) return false;
      if (filterProvider !== "all" && r.provider !== filterProvider) return false;
      if (filterFrom && r.timestamp < filterFrom) return false;
      if (filterTo && r.timestamp > filterTo) return false;
      return true;
    });
  }, [records, filterAction, filterApproval, filterProvider, filterFrom, filterTo]);

  // Stats
  const totalTokensIn = useMemo(
    () => filtered.reduce((s, r) => s + r.input_tokens, 0),
    [filtered],
  );
  const totalTokensOut = useMemo(
    () => filtered.reduce((s, r) => s + r.output_tokens, 0),
    [filtered],
  );
  const actionBreakdown = useMemo(() => {
    const map: Partial<Record<ProvenanceAction, number>> = {};
    for (const r of filtered) {
      map[r.action] = (map[r.action] ?? 0) + 1;
    }
    return map;
  }, [filtered]);

  const handleExport = () => {
    const md = generateAuditMarkdown(filtered);
    downloadText("provenance-audit-report.md", md);
  };

  return (
    <main className="ui-shell provenance-shell">
      <section className="provenance">
        {/* Header */}
        <header className="provenance__header">
          <div className="provenance__header-copy">
            <span className="ui-kicker">Audit</span>
            <h1>Provenance Chain</h1>
            <p>Full audit trail for every AI action. EU AI Act compliance ready.</p>
          </div>
          <div className="provenance__header-actions">
            <button type="button" className="provenance__export-btn" onClick={handleExport}>
              Export audit report
            </button>
          </div>
        </header>

        {/* Stats row */}
        <div className="provenance__stats">
          <StatCard label="Total Actions" value={String(filtered.length)} />
          <StatCard label="Input Tokens" value={formatNumber(totalTokensIn)} />
          <StatCard label="Output Tokens" value={formatNumber(totalTokensOut)} />
          {ALL_ACTIONS.map((action) =>
            actionBreakdown[action] ? (
              <StatCard
                key={action}
                label={ACTION_LABELS[action]}
                value={String(actionBreakdown[action])}
              />
            ) : null,
          )}
        </div>

        {/* Filters */}
        <div className="provenance__filters">
          <label>
            <span>Action</span>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value as ProvenanceAction | "all")}
            >
              <option value="all">All</option>
              {ALL_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABELS[a]}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Provider</span>
            <select
              value={filterProvider}
              onChange={(e) => setFilterProvider(e.target.value)}
            >
              <option value="all">All</option>
              {providers.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Approval</span>
            <select
              value={filterApproval}
              onChange={(e) => setFilterApproval(e.target.value as ApprovalStatus | "all")}
            >
              <option value="all">All</option>
              {ALL_APPROVALS.map((a) => (
                <option key={a} value={a}>
                  {APPROVAL_LABELS[a]}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>From</span>
            <input
              type="date"
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
            />
          </label>

          <label>
            <span>To</span>
            <input
              type="date"
              value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
            />
          </label>
        </div>

        {/* Timeline */}
        <div className="provenance__timeline">
          <div className="provenance__timeline-head">
            <span>Time</span>
            <span>Action</span>
            <span>Provider</span>
            <span>Model</span>
            <span>Tokens</span>
            <span>Duration</span>
            <span>Approval</span>
          </div>
          <div className="provenance__timeline-body">
            {filtered.length === 0 ? (
              <div className="provenance__empty">No records match the current filters.</div>
            ) : (
              filtered.map((r) => <TimelineEntry key={r.id} record={r} />)
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
